import { useState, useCallback, useMemo } from 'react';
import { ORDER_STATUS } from '../constants';
import { BulkActionOrder } from '../types';

type OrderId = string | number;

/** Current selection snapshot returned by `getSelection`. */
export interface OrderSelection {
  ids: OrderId[];
  models: BulkActionOrder[];
  count: number;
  failedCount: number;
}

export interface UseOrdersResult {
  selectedOrders: OrderId[];
  selectedModels: BulkActionOrder[];
  selectedCount: number;
  selectedFailed: number;
  refresh: boolean;
  handleSelection: (ids: OrderId[] | null | undefined, models: BulkActionOrder[] | null | undefined) => void;
  refreshOrders: () => void;
  getSelection: OrderSelection;
}

export function useOrders(): UseOrdersResult {
  const [selectedOrders, setSelectedOrders] = useState<OrderId[]>([]);
  const [selectedModels, setSelectedModels] = useState<BulkActionOrder[]>([]);
  const [selectedFailed, setSelectedFailed] = useState(0);
  const [refresh, setRefresh] = useState(false);

  // Handle order selection
  const handleSelection = useCallback((ids: OrderId[] | null | undefined, models: BulkActionOrder[] | null | undefined) => {
    const newIds = ids || [];
    const newModels = models || [];

    setSelectedOrders(prev => {
      // Check if arrays are the same reference or have same content
      if (prev === newIds || (prev.length === newIds.length && prev.every((id, i) => id === newIds[i]))) {
        return prev;
      }
      return newIds;
    });

    setSelectedModels(prev => {
      // Check if arrays are the same reference or have same content
      if (prev === newModels || (prev.length === newModels.length && prev.every((model, i) => model === newModels[i]))) {
        return prev;
      }
      return newModels;
    });

    const failedCount = newModels.filter(m => m.status === ORDER_STATUS.FAILED).length;
    setSelectedFailed(failedCount);
  }, []);

  const refreshOrders = useCallback(() => {
    setRefresh(prev => !prev);
  }, []);

  const getSelection = useMemo<OrderSelection>(() => ({
    ids: selectedOrders,
    models: selectedModels,
    count: selectedOrders.length,
    failedCount: selectedFailed
  }), [selectedOrders, selectedModels, selectedFailed]);

  return {
    selectedOrders,
    selectedModels,
    selectedCount: selectedOrders.length,
    selectedFailed,
    refresh,
    handleSelection,
    refreshOrders,
    getSelection
  };
}